import moment from "moment-timezone";
import { cc } from "../core/config";
import { RestaurantUtils } from "../restaurant";
import { v4 as uuid } from "uuid";
import { centsToDecimal, percentOfCents } from "../money";
import exprEval from "expr-eval";
import Big from "big.js";
import { logger } from "../logger";
import { orderDish } from "../menu";
function orderCalculateCartTotalDetail(args) {
    const { dishes } = args;
    let total = 0;
    let discount = 0;
    let surcharge = 0;
    dishes.forEach((dish) => {
        const { price, priceDiffSingle } = orderDish.total(dish);
        total += price;
        if (priceDiffSingle < 0) {
            // Price diff is always single unit
            discount += Math.abs(priceDiffSingle * dish.quantity);
        }
        else {
            surcharge += priceDiffSingle * dish.quantity;
        }
    });
    return { total, discount, surcharge };
}
function orderCalculateCartTotal(args) {
    return orderCalculateCartTotalDetail(args).total;
}
/**
 * Calculate the final value of the order
 * - cart: must be the final cart value - promo_old_discount
 * - fees: final list of fees to be applied
 * - discount: final list of discounts to be applied
 */
function orderCalculateTotal(args) {
    const { cart, fees, discounts } = args;
    const feeTotal = fees.reduce((a, fee) => {
        // if voided dont include in price
        if (fee.is_voided) {
            return a;
        }
        return a + fee.value;
    }, 0);
    const discountTotal = discounts.reduce((a, d) => {
        return a + d.value;
    }, 0);
    return Math.max(cart + feeTotal - discountTotal, 0);
}
function orderCalculateFees(args) {
    const { order, orderPaymentType, orderPaymentTypeSub, totalCartWithDiscount, serviceDelivery, serviceDeliveryZones, restaurantFees, isLoggedIn, orderPromo, orderPromoValid, } = args;
    const total = totalCartWithDiscount;
    const df = serviceDelivery;
    const matchingFees = restaurantFees.filter((fee) => {
        if (fee.disabled) {
            return false;
        }
        let conditions = 0;
        let matched = 0;
        const matchAny = fee.match_type === "any";
        if (fee.valid_payments.length > 0) {
            conditions++;
            // IF THE PAYMENT MUST BE MATCHED, THEN DROP FEE UNTIL A PAYMENT METHOD IS SELECTED
            if (!orderPaymentType && !matchAny) {
                return false;
            }
            else if (orderPaymentType && fee.valid_payments.indexOf(orderPaymentType) !== -1) {
                if (orderPaymentType === "windcave" && fee.valid_payments_subtype.length > 0) {
                    if (fee.valid_payments_subtype.indexOf(orderPaymentTypeSub) !== -1) {
                        matched++;
                        if (matchAny)
                            return true;
                    }
                }
                else {
                    matched++;
                    if (matchAny)
                        return true;
                }
            }
        }
        if (fee.valid_services.length > 0) {
            conditions++;
            if (fee.valid_services.indexOf(order.config_service) !== -1) {
                matched++;
                if (matchAny) {
                    return true;
                }
            }
        }
        if (fee.valid_times.length > 0) {
            conditions++;
            if (fee.valid_times.indexOf(order.config_due) !== -1) {
                matched++;
                if (matchAny) {
                    return true;
                }
            }
        }
        if (fee.valid_hours.length > 0) {
            conditions++;
            const datetime = order.config_due === "now"
                ? moment.tz(cc.timezone)
                : moment.tz(order.config_timestamp, cc.timezone);
            const { isWithin } = RestaurantUtils.opening_hours.isTimeWithin({
                dt: datetime,
                timezone: cc.timezone,
                hours: fee.valid_hours,
                first_offset: 0,
                last_offset: 0,
            });
            if (!isWithin) {
                matched++;
                if (matchAny) {
                    return true;
                }
            }
        }
        // IF CONDITIONS MET = CONDITIONS MATCHED, THEN FEE APPLIES
        return conditions === matched;
    });
    const fees = [];
    if (order.config_service === "delivery") {
        if (order.config_zone_id) {
            const zone = serviceDeliveryZones.find((z) => z.id === order.config_zone_id);
            fees.push({
                id: uuid(),
                name: "Delivery Fee",
                value: zone ? zone.price || 0 : 0,
                is_voided: false,
                is_payment_fee: false,
                is_delivery_fee: true,
            });
        }
        else if (df.fee_type === "formula" && df.fee_formula) {
            try {
                const CT = centsToDecimal(totalCartWithDiscount); // needs to be decimal
                const DD = order.config_distance || 0;
                const DT = order.config_driving_time || 0;
                const LI = isLoggedIn ? 1 : 0;
                const evaluation = exprEval.Parser.evaluate(df.fee_formula, { CT, DD, DT, LI });
                const value = parseInt(Big(evaluation).times(100).toFixed(0), 10);
                fees.push({
                    id: uuid(),
                    name: "Delivery Fee",
                    value: value,
                    is_voided: false,
                    is_payment_fee: false,
                    is_delivery_fee: true,
                });
            }
            catch (e) {
                logger.captureException(e);
            }
        }
        else if (df.fee_type === "fixed" && df.fee_fixed_cost) {
            fees.push({
                id: uuid(),
                name: "Delivery Fee",
                value: df.fee_fixed_cost,
                is_voided: false,
                is_payment_fee: false,
                is_delivery_fee: true,
            });
        }
        else if (df.fee_type === "range" && df.fee_ranges.length > 0) {
            const ranges = df.fee_ranges;
            let fee = ranges[0].price;
            for (const range of ranges) {
                if (order.config_distance < range.up_to * 1000) {
                    fee = range.price;
                    break;
                }
            }
            fees.push({
                id: uuid(),
                name: "Delivery Fee",
                value: fee,
                is_voided: false,
                is_payment_fee: false,
                is_delivery_fee: true,
            });
        }
    }
    if (fees[0] && fees[0].is_delivery_fee) {
        let deliveryFeeVoided = df.free_delivery_min
            ? totalCartWithDiscount >= df.free_delivery_min
            : false;
        if (df.fee_order_times &&
            df.fee_order_times.length === 1 &&
            df.fee_order_times[0] !== order.config_due) {
            deliveryFeeVoided = true;
        }
        if (!deliveryFeeVoided && orderPromoValid && orderPromo && orderPromo.free_delivery) {
            deliveryFeeVoided = true;
        }
        if (deliveryFeeVoided) {
            fees[0].is_voided = true;
        }
    }
    for (const fee of matchingFees) {
        const fixed = fee.fixed_value || 0;
        const percent = percentOfCents(total, fee.percent_value);
        const feeTotal = fixed + percent;
        let isPaymentFee = false;
        if (fee.valid_payments.length > 0 &&
            orderPaymentType &&
            fee.valid_payments.indexOf(orderPaymentType) !== -1) {
            isPaymentFee = true;
        }
        fees.push({
            id: uuid(),
            fee_id: fee.id,
            name: fee.name,
            value: feeTotal,
            is_voided: false,
            is_payment_fee: isPaymentFee,
            is_delivery_fee: false,
        });
    }
    return fees;
}
export const orderTotals = {
    cartDetail: orderCalculateCartTotalDetail,
    cart: orderCalculateCartTotal,
    total: orderCalculateTotal,
    fees: orderCalculateFees,
};
//# sourceMappingURL=totals.js.map